import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { convertMoneyToNumber } from '../../util/currency';
import { richText } from '../../util/richText';
import { isBookingProcessAlias } from '../../transactions/transaction';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const formatListingCardPrice = (intl, price) => {
  const valueAsNumber = convertMoneyToNumber(price);
  const isWholeNumber = Number.isInteger(valueAsNumber);
  return intl.formatNumber(valueAsNumber, {
    style: 'currency',
    currency: price.currency,
    minimumFractionDigits: isWholeNumber ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatListingCardPrice(intl, price);
    return { formattedPrice, priceTooltip: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTooltip: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const getLocationLabel = publicData => {
  const location = publicData?.location;
  if (!location) {
    return null;
  }
  if (typeof location === 'string') {
    return location;
  }
  return location.address || location.building || null;
};

const getGuestsCount = publicData =>
  publicData?.guests ?? publicData?.maxGuests ?? publicData?.capacity ?? null;

const getReviewCount = (listing, publicData) =>
  publicData?.reviewCount ?? listing?.attributes?.metadata?.reviewCount ?? null;

const getAverageRating = publicData =>
  publicData?.averageRating ?? publicData?.rating ?? null;

/**
 * Returns all translated and formatted strings for ListingCard so the
 * presentational component can stay simple and aria-labels use the same copy.
 *
 * @param {Object} listing - API entity: listing or ownListing
 * @param {Object} config - app configuration (e.g. from useConfiguration())
 * @param {Object} intl - React Intl instance (e.g. from useIntl())
 * @returns {Object} translations and derived values for the listing card
 */
export const getListingCardTranslations = (listing, config, intl) => {
  const { title = '', price, publicData } = listing?.attributes || {};

  const authorDisplayName = listing?.author?.attributes?.profile?.displayName;
  const authorName = intl.formatMessage(
    { id: 'ListingCard.author' },
    { authorName: authorDisplayName }
  );

  const validListingTypes = config.listing.listingTypes || [];
  const { listingType } = publicData || {};
  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const showPrice = displayPrice(listingTypeConfig);
  const { formattedPrice, priceTooltip } = priceData(price, config.currency, intl);

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);

  const priceMessageId = hasMultiplePriceVariants
    ? 'ListingCard.priceStartingFrom'
    : 'ListingCard.price';

  const perUnitString = isBookable
    ? intl.formatMessage({ id: 'ListingCard.perUnitSlash' }, { unitType: publicData?.unitType })
    : '';

  // Visible price block uses JSX spans for styling; aria-label needs a plain string.
  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? <span className={css.perUnit}>{perUnitString}</span> : '';
  const priceMessage =
    showPrice && formattedPrice != null
      ? intl.formatMessage({ id: priceMessageId }, { priceValue, pricePerUnit })
      : '';

  const priceMessagePlain =
    showPrice && formattedPrice != null
      ? intl.formatMessage(
          { id: priceMessageId },
          { priceValue: formattedPrice, pricePerUnit: perUnitString }
        )
      : '';

  const cardAriaLabel =
    priceMessagePlain.length > 0
      ? intl.formatMessage(
          { id: 'ListingCard.screenreader.label' },
          { listingTitle: title, formattedPrice: priceMessagePlain }
        )
      : title;

  const locationLabel = getLocationLabel(publicData);
  const guestCount = getGuestsCount(publicData);
  const reviewCount = getReviewCount(listing, publicData);
  const averageRating = getAverageRating(publicData);

  const formattedRating =
    typeof averageRating === 'number'
      ? intl.formatNumber(averageRating, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : averageRating;

  let guestsAndReviewsMessage = null;
  if (guestCount != null && reviewCount != null) {
    guestsAndReviewsMessage = intl.formatMessage(
      { id: 'ListingCard.guestsAndReviews' },
      { guestCount, reviewCount }
    );
  } else if (guestCount != null) {
    guestsAndReviewsMessage = intl.formatMessage({ id: 'ListingCard.guests' }, { guestCount });
  } else if (reviewCount != null) {
    guestsAndReviewsMessage = intl.formatMessage({ id: 'ListingCard.reviews' }, { reviewCount });
  }

  const favoriteLabel = intl.formatMessage({ id: 'ListingCard.favorite' });

  return {
    titlePlain: title,
    titleFormatted: richText(title, {
      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
      longWordClass: css.longWord,
    }),
    authorName,
    showPrice,
    priceTooltip,
    priceMessage,
    cardAriaLabel,
    locationLabel,
    formattedRating,
    guestsAndReviewsMessage,
    favoriteLabel,
  };
};
